/**
 * Market page volume panel (#874): 24 h (asset ctxs), last hour and lifetime (1h candles) for one outcome.
 * Data: useMarketVolume (one cached ctxs query + one throttled candle request per 60 s). `volume.trend` is computed
 * but not shown (future "trending" flag).
 */
import type { ReactNode } from 'react';
import { dateTimeFormat } from '../i18n/format';
import { useT } from '../i18n/useT';
import { formatVolumeCount, formatVolumeUsd } from './format';
import { useMarketVolume } from './useVolume';
import { CANDLE_CAP } from './volume';

export default function VolumePanel({ outcome }: { outcome: number }) {
  const t = useT();
  const { volume, loading, error } = useMarketVolume(outcome);
  const { day, lastHour, lifetime } = volume;
  const capped = lifetime?.capped ?? false;
  const atLeast = (s: string) => (capped && s !== '—' ? t('volume.at_least', { amount: s }) : s);
  const approx = (s: string) => (s !== '—' ? t('volume.approx', { amount: s }) : s);
  const dash = '—';

  const row = (key: string, label: string, usd: string, contracts: string, trades: string): ReactNode => (
    <tr key={key} data-testid={`volume-row-${key}`} className="border-t border-border/60">
      <th scope="row" className="py-1.5 pe-3 text-start font-normal text-muted">
        {label}
      </th>
      <td className="num py-1.5 pe-3 text-end font-medium" data-testid={`volume-${key}-usd`}>
        {usd}
      </td>
      <td className="num py-1.5 pe-3 text-end" data-testid={`volume-${key}-contracts`}>
        {contracts}
      </td>
      <td className="num py-1.5 text-end" data-testid={`volume-${key}-trades`}>
        {trades}
      </td>
    </tr>
  );

  const hourFrom = lastHour ? dateTimeFormat({ hour: '2-digit', minute: '2-digit' }).format(new Date(lastHour.fromMs)) : '';
  const since =
    lifetime?.firstMs !== undefined
      ? dateTimeFormat({ year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(lifetime.firstMs))
      : undefined;

  return (
    <div className="card p-4" data-testid="volume-panel">
      <h2 className="mb-2 text-sm font-semibold">{t('volume.title')}</h2>
      {error ? (
        <p className="text-sm text-muted" role="status">
          {t('volume.unavailable')}
        </p>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-muted">
                <th scope="col" className="pb-1 pe-3 text-start font-normal">
                  {t('volume.col.period')}
                </th>
                <th scope="col" className="pb-1 pe-3 text-end font-normal">
                  {t('volume.col.usd')}
                </th>
                <th scope="col" className="pb-1 pe-3 text-end font-normal">
                  {t('volume.col.contracts')}
                </th>
                <th scope="col" className="pb-1 text-end font-normal">
                  {t('volume.col.trades')}
                </th>
              </tr>
            </thead>
            <tbody>
              {row('day', t('volume.row.day'), day ? formatVolumeUsd(day.usd) : dash, day ? formatVolumeCount(day.contracts) : dash, dash)}
              {row(
                'hour',
                t('volume.row.hour'),
                lastHour ? approx(formatVolumeUsd(lastHour.usd)) : dash,
                lastHour ? formatVolumeCount(lastHour.contracts) : dash,
                lastHour ? formatVolumeCount(lastHour.trades) : dash,
              )}
              {row(
                'lifetime',
                t('volume.row.lifetime'),
                lifetime ? (capped ? atLeast(formatVolumeUsd(lifetime.usd)) : approx(formatVolumeUsd(lifetime.usd))) : dash,
                lifetime ? atLeast(formatVolumeCount(lifetime.contracts)) : dash,
                lifetime ? atLeast(formatVolumeCount(lifetime.trades)) : dash,
              )}
            </tbody>
          </table>
          {loading ? (
            <p className="mt-2 text-xs text-muted" role="status">
              {t('volume.loading')}
            </p>
          ) : (
            <ul className="mt-2 space-y-0.5 text-[11px] text-muted" data-testid="volume-notes">
              {lastHour ? <li>{t('volume.hour_note', { from: hourFrom })}</li> : null}
              {since ? <li>{t('volume.lifetime_since', { date: since })}</li> : null}
              {capped ? <li data-testid="volume-capped">{t('volume.lifetime_capped', { n: CANDLE_CAP })}</li> : null}
              <li>{t('volume.sides_note')}</li>
            </ul>
          )}
        </>
      )}
    </div>
  );
}
